import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, AlertCircle } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from './Button';
import { NotificationDropdown } from './NotificationDropdown';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { ROUTES } from '../constants';

interface HeaderProps {
  theme?: 'blue' | 'green';
  showNotifications?: boolean;
  notificationCount?: number;
  showProfileAlert?: boolean;
  onProfileAlertClick?: () => void;
  onNotificationClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  theme = 'blue',
  showNotifications = false,
  notificationCount,
  showProfileAlert = false,
  onProfileAlertClick,
  onNotificationClick,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { unreadCount } = useNotifications();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  const themeColors = {
    blue: {
      notificationHover: 'hover:text-blue-600 hover:bg-blue-50',
      notificationDot: 'bg-blue-600',
    },
    green: {
      notificationHover: 'hover:text-green-600 hover:bg-green-50',
      notificationDot: 'bg-green-600',
    },
  };

  const colors = themeColors[theme];

  const displayNotificationCount = notificationCount !== undefined ? notificationCount : unreadCount;

  const handleNotificationClick = () => {
    if (onNotificationClick) {
      onNotificationClick();
    } else {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Logo size="md" />

          <div className="flex items-center gap-4">
            {showNotifications && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className={`p-2 text-gray-600 ${colors.notificationHover} rounded-lg transition-colors relative`}
                >
                  <Bell className="h-5 w-5" />
                  {displayNotificationCount > 0 && (
                    <span className={`absolute top-1 right-1 w-2 h-2 ${colors.notificationDot} rounded-full`}></span>
                  )}
                </button>
                <NotificationDropdown
                  isOpen={isDropdownOpen}
                  onClose={() => setIsDropdownOpen(false)}
                  theme={theme}
                />
              </div>
            )}
            {showProfileAlert && (
              <button
                onClick={onProfileAlertClick}
                className="relative"
                aria-label="Profile incomplete"
              >
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
                <AlertCircle className="h-5 w-5 text-red-500" />
              </button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
